/**
 * Boot-on-interaction logic for the lean marketing entry.
 *
 * What: Decides when the prerendered marketing page loads the full app shell
 * (immediately for signed-in visitors, on first interaction otherwise), and
 * captures pre-boot CTA clicks so the app can replay them after mount.
 * Why: PageSpeed counts the entire app shell as unused JavaScript on `/`;
 * anonymous visitors who never interact should never download it. Logic lives
 * here (imported by the `src/marketingBoot.ts` entry) so jsdom tests can
 * exercise it with injected deps and without entry-module side effects.
 * When: Update when boot triggers, CTA capture/replay contract, or the
 * pre-boot analytics surface change.
 *
 * Hard constraint: this module's import graph must stay free of React/MUI —
 * pulling either in would put the app shell back on the marketing page's
 * critical path. Keep imports limited to dependency-free utils.
 */
import { isChunkLoadError } from './chunkLoadError';
import { INTERACTION_EVENTS } from './deferNonCritical';

export interface LeanBootCtaIntent {
  value: string;
  index: number;
}

declare global {
  interface Window {
    /** Pending pre-boot CTA click, replayed by useLeanBootCtaReplay. */
    __DT_LEAN_CTA__?: LeanBootCtaIntent;
    /** Stops pre-boot CTA recording; installed by initLeanMarketingBoot. */
    __DT_LEAN_CTA_STOP__?: () => void;
  }
}

export const LEAN_BOOT_CTA_ATTR = 'data-boot-cta';

// One reload max per tab when a stale edge-cached page references dead chunk
// hashes — stricter than lazyWithChunkRetry's unconditional reload because
// nothing here can render an error state if reloading loops.
export const LEAN_BOOT_RELOAD_GUARD_KEY = 'dt:leanBoot:reloaded';

export interface LeanMarketingBootDeps {
  importShell: () => Promise<unknown>;
  hasAuth0Session: () => boolean;
  scheduleNonCritical: (fn: () => void) => void;
  initializeMetaPixel: (pixelId: string) => Promise<void>;
  initializePlausible: (args: {
    domain: string;
    isAuthenticated: boolean;
  }) => Promise<void>;
  metaPixelId?: string;
  plausibleDomain?: string;
  reload: () => void;
}

const readReloadGuard = (): boolean => {
  try {
    return sessionStorage.getItem(LEAN_BOOT_RELOAD_GUARD_KEY) === 'true';
  } catch {
    // Blocked storage: behave as if a reload already happened so a broken
    // private-mode tab can never reload-loop.
    return true;
  }
};

const writeReloadGuard = (): void => {
  try {
    sessionStorage.setItem(LEAN_BOOT_RELOAD_GUARD_KEY, 'true');
  } catch {
    // Ignore: the guard read treats blocked storage as already-reloaded.
  }
};

const clearReloadGuard = (): void => {
  try {
    sessionStorage.removeItem(LEAN_BOOT_RELOAD_GUARD_KEY);
  } catch {
    // Ignore.
  }
};

/**
 * End pre-boot recording without consuming an intent that still needs replay.
 * The layout effect calls this at commit, before React controls accept input.
 */
export const stopLeanBootCtaRecording = (): void => {
  if (typeof window === 'undefined') return;
  const stop = window.__DT_LEAN_CTA_STOP__;
  delete window.__DT_LEAN_CTA_STOP__;
  stop?.();
};

/**
 * Read and clear the pending pre-boot CTA intent. Called by the app-side
 * replay hook when replay executes; also ends recording so CTA clicks in the
 * booted app are never re-captured or restyled.
 */
export const consumeLeanBootCtaIntent = (): LeanBootCtaIntent | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  stopLeanBootCtaRecording();
  const intent = window.__DT_LEAN_CTA__ ?? null;
  delete window.__DT_LEAN_CTA__;
  return intent;
};

// Keys that only move focus or scroll within the static snapshot. Booting on
// these would yank the just-focused element out from under a keyboard user
// when main.tsx's clean mount replaces the tree (Tab lands on a prerendered
// node, then focus resets to <body>). Pure-keyboard visitors stay in the
// snapshot until they press a real activation key (Enter, Space, a character…).
const NON_ACTIVATING_KEYS = new Set<string>([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Escape',
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
]);

const isNativeLink = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('a[href]') !== null;

const isActivationKey = (event: Event): boolean =>
  event instanceof KeyboardEvent &&
  (event.key === 'Enter' || event.key === ' ');

const isSecondaryPointer = (event: Event): boolean =>
  event instanceof MouseEvent && event.button !== 0;

export const initLeanMarketingBoot = (
  deps: LeanMarketingBootDeps
): { teardown: () => void } => {
  const cleanups: Array<() => void> = [];
  let bootStarted = false;

  const startBoot = () => {
    if (bootStarted) {
      return;
    }
    bootStarted = true;

    deps
      .importShell()
      .catch(error => {
        if (!isChunkLoadError(error)) {
          throw error;
        }
        return deps.importShell();
      })
      .then(clearReloadGuard)
      .catch(error => {
        if (isChunkLoadError(error) && !readReloadGuard()) {
          writeReloadGuard();
          deps.reload();
          return;
        }
        // No Sentry exists pre-boot; console is the only surface.
        console.error('Marketing shell boot failed:', error);
      });
  };

  const triggerBoot = () => {
    for (const cleanup of cleanups) {
      cleanup();
    }
    startBoot();
  };

  const armInteractionListeners = () => {
    for (const eventName of INTERACTION_EVENTS) {
      const onInteraction = (event: Event) => {
        // A native link already works. Booting between its press and click
        // could remove the anchor before the browser follows its href.
        if (
          (eventName === 'pointerdown' ||
            eventName === 'touchstart' ||
            eventName === 'keydown') &&
          (isNativeLink(event.target) || isSecondaryPointer(event))
        ) {
          return;
        }
        // Navigation-only keys (Tab, arrows, paging, bare modifiers) must not
        // boot: they focus/scroll the snapshot, and the clean mount would then
        // steal that focus. Keep listening for a later activation key.
        if (
          eventName === 'keydown' &&
          event instanceof KeyboardEvent &&
          NON_ACTIVATING_KEYS.has(event.key)
        ) {
          return;
        }
        if (
          eventName === 'pointerdown' ||
          eventName === 'touchstart' ||
          isActivationKey(event)
        ) {
          recordCtaIntent(event.target);
        }
        triggerBoot();
      };
      // Ignored link presses/navigation keys must not disarm a later boot.
      // triggerBoot removes all listeners after the first eligible interaction.
      // Mirrors deferNonCritical: capture so nested-container scrolls count,
      // passive so the gesture is never delayed.
      window.addEventListener(eventName, onInteraction, {
        capture: true,
        passive: true,
      });
      cleanups.push(() =>
        window.removeEventListener(eventName, onInteraction, { capture: true })
      );
    }
  };

  // A pre-boot CTA activation can't open its dialog (no React yet), so remember
  // which element was activated, show a pressed affordance, and let
  // useLeanBootCtaReplay re-activate the re-rendered element after mount.
  const recordCtaIntent = (target: EventTarget | null) => {
    if (!(target instanceof Element) || isNativeLink(target)) {
      return;
    }
    const ctaElement = target.closest<HTMLElement>(`[${LEAN_BOOT_CTA_ATTR}]`);
    if (!ctaElement) {
      return;
    }
    const value = ctaElement.getAttribute(LEAN_BOOT_CTA_ATTR) ?? '';
    const peers = Array.from(
      document.querySelectorAll(`[${LEAN_BOOT_CTA_ATTR}="${value}"]`)
    );
    window.__DT_LEAN_CTA__ = {
      value,
      index: Math.max(0, peers.indexOf(ctaElement)),
    };
    // Pressed + busy affordance that keeps full text contrast: the pushable
    // press (DESIGN.md §9/§10 — translateY(2px)) plus aria-busy/cursor, not an
    // opacity fade that would drop the coral CTA's white text below ~2:1.
    ctaElement.style.transform = 'translateY(2px)';
    ctaElement.style.cursor = 'progress';
    ctaElement.setAttribute('aria-busy', 'true');
  };

  // Keep recording presses while a shell started by an earlier interaction
  // is still loading. Enter/Space need the same early capture as pointers:
  // a cached mount can replace the focused button before its click arrives.
  const onDocumentPress = (event: Event) => {
    if (isSecondaryPointer(event)) return;
    recordCtaIntent(event.target);
  };
  const onDocumentKeyDown = (event: KeyboardEvent) => {
    if (isActivationKey(event)) recordCtaIntent(event.target);
  };

  // Assistive tech and programmatic activation can emit `click` with none of
  // the armed interaction events — so the click handler must both record intent
  // and start the boot, or the CTA looks pressed while nothing ever loads.
  const onDocumentClick = (event: MouseEvent) => {
    if (isNativeLink(event.target) || isSecondaryPointer(event)) return;
    recordCtaIntent(event.target);
    triggerBoot();
  };

  const stopCtaRecording = () => {
    document.removeEventListener('pointerdown', onDocumentPress, {
      capture: true,
    });
    document.removeEventListener('touchstart', onDocumentPress, {
      capture: true,
    });
    document.removeEventListener('keydown', onDocumentKeyDown, {
      capture: true,
    });
    document.removeEventListener('click', onDocumentClick, { capture: true });
    delete window.__DT_LEAN_CTA_STOP__;
  };

  document.addEventListener('pointerdown', onDocumentPress, {
    capture: true,
  });
  document.addEventListener('touchstart', onDocumentPress, {
    capture: true,
    passive: true,
  });
  document.addEventListener('keydown', onDocumentKeyDown, { capture: true });
  document.addEventListener('click', onDocumentClick, { capture: true });
  window.__DT_LEAN_CTA_STOP__ = stopCtaRecording;

  if (deps.hasAuth0Session()) {
    // Signed-in visitors get today's exact behavior: the shell boots at once
    // and LandingPage's redirect effect forwards them to their app home.
    startBoot();
  } else {
    armInteractionListeners();
  }

  // Analytics parity with main.tsx: same env gating, same defer scheduling.
  // Both initializers are idempotent, so the later shell boot re-scheduling
  // them is a no-op.
  if (deps.metaPixelId) {
    const metaPixelId = deps.metaPixelId;
    deps.scheduleNonCritical(() => {
      deps.initializeMetaPixel(metaPixelId).catch(err => {
        console.warn('Failed to initialize Meta Pixel:', err);
      });
    });
  }

  if (deps.plausibleDomain) {
    const plausibleDomain = deps.plausibleDomain;
    deps.scheduleNonCritical(() => {
      deps
        .initializePlausible({
          domain: plausibleDomain,
          // Skip tracking for our own signed-in admin/student traffic.
          isAuthenticated: deps.hasAuth0Session(),
        })
        .catch(err => {
          console.warn('Failed to initialize Plausible:', err);
        });
    });
  }

  return {
    teardown: () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
      stopCtaRecording();
    },
  };
};
